(function() {
    "use strict";
    window.SunClipImageLayering = function(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.ctx = this.canvas.getContext("2d");
        this.layers = new Map;
        this.baseImage = null;
        this.scale = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.currentScale = 1;
        this.currentOffsetX = 0;
        this.currentOffsetY = 0;
        this.lockYAxis = false;
        this.initialScale = null;
        this.userHasInteracted = false;
        this.imageCache = new Map;
        this.preloadedImages = new Set;
        this.loadingImages = new Set;
        this.setupCanvas();
        this.setupEventListeners();
        this.startPreloading();
        this.lastContainerWidth = 0;
        this.lastContainerHeight = 0;
        this.isResizing = false;
        return this;
    };
    window.SunClipImageLayering.prototype = {
        setupCanvas: function() {
            this.pixelRatio = window.devicePixelRatio || 1;
            this.resizeCanvas();
            const self = this;
            let resizeTimeout;
            const isMobile = window.innerWidth <= 768;
            const debounceDelay = isMobile ? 300 : 100;
            window.addEventListener("resize", function() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(function() {
                    self.resizeCanvas();
                }, debounceDelay);
            });
        },
        setupEventListeners: function() {
            let isMouseDown = false;
            let lastX = 0;
            let lastY = 0;
            const self = this;
            this.canvas.addEventListener("mousedown", function(e) {
                isMouseDown = true;
                lastX = e.clientX;
                lastY = e.clientY;
                self.canvas.style.cursor = "grabbing";
            });
            this.canvas.addEventListener("mousemove", function(e) {
                if (isMouseDown) {
                    const deltaX = e.clientX - lastX;
                    const deltaY = e.clientY - lastY;
                    self.pan(deltaX, deltaY);
                    lastX = e.clientX;
                    lastY = e.clientY;
                }
            });
            this.canvas.addEventListener("mouseup", function() {
                isMouseDown = false;
                self.canvas.style.cursor = "grab";
            });
            this.canvas.addEventListener("mouseleave", function() {
                isMouseDown = false;
                self.canvas.style.cursor = "default";
            });
            this.canvas.addEventListener("wheel", function(e) {
                e.preventDefault();
                const zoomFactor = e.deltaY > 0 ? .9 : 1.1;
                const zoomX = e.offsetX;
                const zoomY = self.lockYAxis ? self.canvas.height / self.pixelRatio / 2 : e.offsetY;
                self.zoom(zoomFactor, zoomX, zoomY);
            });
            this.setupTouchEvents();
        },
        setupTouchEvents: function() {
            if (window.innerWidth <= 768) {
                console.log("[Canvas] Touch events disabled on mobile for scroll compatibility");
                return;
            }
            let touchStartDistance = 0;
            let touchStartScale = 1;
            let lastTouchX = 0;
            let lastTouchY = 0;
            const self = this;
            this.canvas.addEventListener("touchstart", function(e) {
                e.preventDefault();
                if (e.touches.length === 1) {
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                } else if (e.touches.length === 2) {
                    touchStartDistance = self.getTouchDistance(e.touches);
                    touchStartScale = self.scale;
                }
            });
            this.canvas.addEventListener("touchmove", function(e) {
                e.preventDefault();
                if (e.touches.length === 1) {
                    const deltaX = e.touches[0].clientX - lastTouchX;
                    const deltaY = e.touches[0].clientY - lastTouchY;
                    self.pan(deltaX, deltaY);
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                } else if (e.touches.length === 2) {
                    const currentDistance = self.getTouchDistance(e.touches);
                    const zoomFactor = currentDistance / touchStartDistance;
                    self.setScale(touchStartScale * zoomFactor);
                }
            });
        },
        getTouchDistance: function(touches) {
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        },
        resizeCanvas: function() {
            if (this.isResizing || window.sunClipConfigurator && window.sunClipConfigurator.isChangingSticky) {
                return;
            }
            const now = Date.now();
            const isMobile = window.innerWidth <= 768;
            const minInterval = isMobile ? 200 : 50;
            if (this._lastResizeTime && now - this._lastResizeTime < minInterval) {
                return;
            }
            this._lastResizeTime = now;
            if (window.sunClipConfigurator && window.sunClipConfigurator.lastViewportWidth) {
                const currentViewportWidth = window.innerWidth;
                if (Math.abs(currentViewportWidth - window.sunClipConfigurator.lastViewportWidth) < 5) {
                    return;
                }
                window.sunClipConfigurator.lastViewportWidth = currentViewportWidth;
            }
            const container = this.canvas.parentElement;
            const rect = container.getBoundingClientRect();
            const cs = window.getComputedStyle(container);
            const width = Math.max(0, Math.round(rect.width - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0)));
            const height = Math.max(0, Math.round(rect.height - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0)));
            if (width < 1 || height < 1) {
                return;
            }
            if (Math.abs(width - this.lastContainerWidth) < 5 && Math.abs(height - this.lastContainerHeight) < 5) {
                return;
            }
            this.isResizing = true;
            this.lastContainerWidth = width;
            this.lastContainerHeight = height;
            this.canvas.style.width = width + "px";
            this.canvas.style.height = height + "px";
            this.canvas.width = width * this.pixelRatio;
            this.canvas.height = height * this.pixelRatio;
            this.ctx.scale(this.pixelRatio, this.pixelRatio);
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = "high";
            this.render();
            const self = this;
            setTimeout(function() {
                self.isResizing = false;
            }, 50);
        },
        loadImage: function(src, showProgress) {
            const self = this;
            if (this.imageCache.has(src)) {
                return Promise.resolve(this.imageCache.get(src));
            }
            if (this.loadingImages.has(src)) {
                return new Promise(function(resolve) {
                    const checkForImage = function() {
                        if (self.imageCache.has(src)) {
                            resolve(self.imageCache.get(src));
                        } else if (self.loadingImages.has(src)) {
                            setTimeout(checkForImage, 50);
                        }
                    };
                    checkForImage();
                });
            }
            this.loadingImages.add(src);
            var timeout = 1e4;
            return new Promise(function(resolve, reject) {
                const img = new Image;
                var timeoutId = null;
                var settled = false;
                if (showProgress) {
                    self.updateLoadingMessage("Loading " + src.split("/").pop() + "...");
                }
                timeoutId = setTimeout(function() {
                    if (!settled) {
                        settled = true;
                        console.warn("Image load timeout after " + timeout + "ms: " + src);
                        self.loadingImages.delete(src);
                        img.src = "";
                        reject(new Error("Timeout loading image: " + src));
                    }
                }, timeout);
                img.onload = function() {
                    if (!settled) {
                        settled = true;
                        clearTimeout(timeoutId);
                        self.imageCache.set(src, img);
                        self.loadingImages.delete(src);
                        self.preloadedImages.add(src);
                        resolve(img);
                    }
                };
                img.onerror = function() {
                    if (!settled) {
                        settled = true;
                        clearTimeout(timeoutId);
                        console.error("Failed to load image: " + src);
                        self.loadingImages.delete(src);
                        reject(new Error("Failed to load image: " + src));
                    }
                };
                img.src = src;
            });
        },
        loadBaseImage: function(src) {
            const self = this;
            this.showLoading();
            this.loadImage(src, true).then(function(image) {
                self.baseImage = image;
                self.fitToCanvas();
                if (self.lockYAxis) {
                    self.centerImage();
                }
                self.render();
                self.hideLoading();
            }).catch(function(error) {
                console.error("Error loading base image:", error);
                self.hideLoading();
            });
        },
        updateLayer: function(layerId, src, zIndex) {
            const self = this;
            zIndex = zIndex || 1;
            if (!src || typeof src !== "string") {
                console.warn("[Canvas] Invalid src for layer " + layerId + ": empty or not a string");
                return;
            }
            if (src.indexOf("undefined") !== -1 || src.indexOf("null") !== -1) {
                console.warn("[Canvas] Invalid src for layer " + layerId + ": contains undefined/null -", src);
                return;
            }
            var isValidUrl = src.indexOf("cdn.shopify.com") !== -1 || src.indexOf("/cdn/") !== -1 || src.indexOf("://") !== -1 || src.indexOf("//") === 0;
            if (!isValidUrl) {
                console.warn("[Canvas] Asset URL not resolved for layer " + layerId + ":", src);
                return;
            }
            const isPreloaded = this.imageCache.has(src);
            if (!isPreloaded) {
                this.showLoading();
            }
            this.loadImage(src, !isPreloaded).then(function(image) {
                self.layers.set(layerId, {
                    image: image,
                    zIndex: zIndex
                });
                self.render();
                if (!isPreloaded) {
                    self.hideLoading();
                }
            }).catch(function(error) {
                console.error("Error updating layer " + layerId + ":", error);
                if (error.message && error.message.indexOf("Timeout") === -1) {
                    self.showErrorMessage("Failed to load " + layerId + " image");
                }
                if (!isPreloaded) {
                    self.hideLoading();
                }
            });
        },
        removeLayer: function(layerId) {
            this.layers.delete(layerId);
            this.render();
        },
        clearAllLayers: function() {
            this.layers.clear();
            this.render();
        },
        updateClipType: function(clipType) {
            this.removeLayer("lens");
        },
        fitToCanvas: function() {
            if (!this.baseImage) return;
            this.setViewport("full");
        },
        centerImage: function() {
            if (!this.baseImage) return;
            const canvasWidth = this.canvas.width / this.pixelRatio;
            const canvasHeight = this.canvas.height / this.pixelRatio;
            this.offsetX = (canvasWidth - this.baseImage.width * this.scale) / 2;
            this.offsetY = (canvasHeight - this.baseImage.height * this.scale) / 2;
            this.render();
        },
        setViewport: function(viewType) {
            if (!this.baseImage) return;
            this.userHasInteracted = false;
            const viewports = {
                full: {
                    x1: 57,
                    y1: 417,
                    x2: 1918,
                    y2: 1102
                },
                bridge: {
                    x1: 998,
                    y1: 504,
                    x2: 1710,
                    y2: 940
                },
                magnet: {
                    x1: 526,
                    y1: 344,
                    x2: 1990,
                    y2: 1077
                },
                crystal: {
                    x1: 526,
                    y1: 344,
                    x2: 1990,
                    y2: 1077
                }
            };
            const viewport = viewports[viewType] || viewports["full"];
            const centerX = (viewport.x1 + viewport.x2) / 2;
            const centerY = (viewport.y1 + viewport.y2) / 2;
            const viewWidth = Math.abs(viewport.x2 - viewport.x1);
            const viewHeight = Math.abs(viewport.y2 - viewport.y1);
            const canvasWidth = this.canvas.width / this.pixelRatio;
            const canvasHeight = this.canvas.height / this.pixelRatio;
            const scaleX = canvasWidth / viewWidth;
            const scaleY = canvasHeight / viewHeight;
            this.scale = Math.min(scaleX, scaleY) * .95;
            if (viewType === "full" && !this.defaultFullScale) {
                this.defaultFullScale = this.scale;
            }
            if (!this.initialScale) {
                this.initialScale = this.scale;
            }
            this.offsetX = canvasWidth / 2 - centerX * this.scale;
            if (this.lockYAxis) {
                this.offsetY = (canvasHeight - this.baseImage.height * this.scale) / 2;
            } else {
                this.offsetY = canvasHeight / 2 - centerY * this.scale;
            }
            this.animateViewportChange();
        },
        animateViewportChange: function(targetScale, targetOffsetX, targetOffsetY) {
            targetScale = targetScale !== undefined ? targetScale : this.scale;
            targetOffsetX = targetOffsetX !== undefined ? targetOffsetX : this.offsetX;
            targetOffsetY = targetOffsetY !== undefined ? targetOffsetY : this.offsetY;
            const startScale = this.currentScale || this.scale;
            const startOffsetX = this.currentOffsetX || this.offsetX;
            const startOffsetY = this.currentOffsetY || this.offsetY;
            const duration = 300;
            const startTime = Date.now();
            const self = this;
            const animate = function() {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeProgress = self.easeInOutCubic(progress);
                self.scale = startScale + (targetScale - startScale) * easeProgress;
                self.offsetX = startOffsetX + (targetOffsetX - startOffsetX) * easeProgress;
                self.offsetY = startOffsetY + (targetOffsetY - startOffsetY) * easeProgress;
                self.currentScale = self.scale;
                self.currentOffsetX = self.offsetX;
                self.currentOffsetY = self.offsetY;
                self.render();
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            requestAnimationFrame(animate);
        },
        easeInOutCubic: function(t) {
            return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        },
        pan: function(deltaX, deltaY) {
            this.userHasInteracted = true;
            const newOffsetX = this.offsetX + deltaX;
            const newOffsetY = this.lockYAxis ? this.offsetY : this.offsetY + deltaY;
            if (this.baseImage) {
                const canvasWidth = this.canvas.width / this.pixelRatio;
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const imageWidth = this.baseImage.width * this.scale;
                const imageHeight = this.baseImage.height * this.scale;
                const overflowX = imageWidth * .3;
                const overflowY = imageHeight * .3;
                const minOffsetX = canvasWidth - imageWidth - overflowX;
                const minOffsetY = canvasHeight - imageHeight - overflowY;
                const maxOffsetX = overflowX;
                const maxOffsetY = overflowY;
                this.offsetX = Math.min(maxOffsetX, Math.max(minOffsetX, newOffsetX));
                if (!this.lockYAxis) {
                    this.offsetY = Math.min(maxOffsetY, Math.max(minOffsetY, newOffsetY));
                }
            } else {
                this.offsetX = newOffsetX;
                if (!this.lockYAxis) {
                    this.offsetY = newOffsetY;
                }
            }
            this.render();
        },
        zoom: function(factor, centerX, centerY, programmatic) {
            if (!programmatic) {
                this.userHasInteracted = true;
            }
            const oldScale = this.scale;
            const minScale = this.initialScale || .5;
            const newScale = Math.max(minScale, Math.min(5, this.scale * factor));
            if (Math.abs(newScale - oldScale) < .001) return;
            const scaleChange = newScale / oldScale;
            this.scale = newScale;
            this.offsetX = centerX - (centerX - this.offsetX) * scaleChange;
            if (this.lockYAxis) {
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const imageHeight = this.baseImage.height * this.scale;
                this.offsetY = (canvasHeight - imageHeight) / 2;
            } else {
                this.offsetY = centerY - (centerY - this.offsetY) * scaleChange;
            }
            if (this.baseImage) {
                const canvasWidth = this.canvas.width / this.pixelRatio;
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const imageWidth = this.baseImage.width * this.scale;
                const imageHeight = this.baseImage.height * this.scale;
                const overflowX = imageWidth * .3;
                const overflowY = imageHeight * .3;
                if (imageWidth < canvasWidth) {
                    this.offsetX = (canvasWidth - imageWidth) / 2;
                } else {
                    const maxOffsetX = overflowX;
                    const minOffsetX = canvasWidth - imageWidth - overflowX;
                    this.offsetX = Math.min(maxOffsetX, Math.max(minOffsetX, this.offsetX));
                }
                if (imageHeight < canvasHeight) {
                    this.offsetY = (canvasHeight - imageHeight) / 2;
                } else if (!this.lockYAxis) {
                    const maxOffsetY = overflowY;
                    const minOffsetY = canvasHeight - imageHeight - overflowY;
                    this.offsetY = Math.min(maxOffsetY, Math.max(minOffsetY, this.offsetY));
                }
            }
            this.render();
        },
        setScale: function(newScale) {
            const minScale = this.initialScale || .5;
            this.scale = Math.max(minScale, Math.min(5, newScale));
            this.render();
        },
        resetView: function() {
            this.userHasInteracted = false;
            if (window.orderFlowConfigurator && window.orderFlowConfigurator.applyStepViewport) {
                window.orderFlowConfigurator.applyStepViewport(window.orderFlowConfigurator.currentStep);
            } else if (window.sunClipConfigurator) {
                window.sunClipConfigurator.updateViewport();
            } else {
                this.setViewport("full");
            }
        },
        render: function() {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.save();
            this.ctx.translate(this.offsetX, this.offsetY);
            this.ctx.scale(this.scale, this.scale);
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = "high";
            if (this.baseImage) {
                this.ctx.drawImage(this.baseImage, 0, 0);
            }
            const sortedLayers = Array.from(this.layers.entries()).sort(function(a, b) {
                return a[1].zIndex - b[1].zIndex;
            });
            for (let i = 0; i < sortedLayers.length; i++) {
                const layer = sortedLayers[i][1];
                if (layer.image) {
                    this.ctx.drawImage(layer.image, 0, 0);
                }
            }
            this.ctx.restore();
        },
        generatePreview: function() {
            const self = this;
            return new Promise(function(resolve) {
                const previewCanvas = document.createElement("canvas");
                const previewCtx = previewCanvas.getContext("2d");
                if (!self.baseImage) {
                    resolve(null);
                    return;
                }
                previewCanvas.width = self.baseImage.width;
                previewCanvas.height = self.baseImage.height;
                previewCtx.drawImage(self.baseImage, 0, 0);
                const sortedLayers = Array.from(self.layers.entries()).sort(function(a, b) {
                    return a[1].zIndex - b[1].zIndex;
                });
                for (let i = 0; i < sortedLayers.length; i++) {
                    const layer = sortedLayers[i][1];
                    if (layer.image) {
                        previewCtx.drawImage(layer.image, 0, 0);
                    }
                }
                resolve(previewCanvas.toDataURL("image/png", .9));
            });
        },
        showLoading: function() {
            const loadingOverlay = document.getElementById("loadingOverlay");
            if (loadingOverlay) {
                loadingOverlay.classList.add("active");
            }
        },
        hideLoading: function() {
            const loadingOverlay = document.getElementById("loadingOverlay");
            if (loadingOverlay) {
                loadingOverlay.classList.remove("active");
            }
        },
        showErrorMessage: function(message) {
            const errorDiv = document.createElement("div");
            errorDiv.className = "error-message";
            errorDiv.textContent = message;
            errorDiv.style.cssText = `\n                position: fixed;\n                top: 20px;\n                right: 20px;\n                background-color: #f44336;\n                color: white;\n                padding: 15px 20px;\n                border-radius: 4px;\n                box-shadow: 0 4px 6px rgba(0,0,0,0.1);\n                z-index: 1000;\n                animation: slideIn 0.3s ease;\n            `;
            document.body.appendChild(errorDiv);
            setTimeout(function() {
                errorDiv.remove();
            }, 5e3);
        },
        startPreloading: function() {
            this.preloadCommonImages();
        },
        preloadCommonImages: function() {
            const self = this;
            const waitForData = function() {
                if (window.sunClipConfiguratorData) {
                    const configuratorData = window.sunClipConfiguratorData;
                    const imagesToPreload = [];
                    [ "solid", "mirror", "gradient" ].forEach(function(category) {
                        if (configuratorData.lensColors[category]) {
                            configuratorData.lensColors[category].slice(0, 3).forEach(function(color) {
                                const fileName = "Raen-Angle-Lens-" + color.id + ".png";
                            });
                        }
                    });
                } else {
                    setTimeout(waitForData, 100);
                }
            };
            setTimeout(waitForData, 1e3);
        },
        updateLoadingMessage: function(message) {
            const loadingOverlay = document.querySelector(".loading-overlay");
            const existingMessage = loadingOverlay && loadingOverlay.querySelector(".loading-message");
            if (loadingOverlay && existingMessage) {
                existingMessage.textContent = message;
            } else if (loadingOverlay) {
                const messageDiv = document.createElement("div");
                messageDiv.className = "loading-message";
                messageDiv.textContent = message;
                messageDiv.style.cssText = `\n                    margin-top: 10px;\n                    font-size: 0.9rem;\n                    color: #666;\n                `;
                loadingOverlay.appendChild(messageDiv);
            }
        },
        zoomIn: function() {
            const centerX = this.canvas.width / this.pixelRatio / 2;
            const centerY = this.canvas.height / this.pixelRatio / 2;
            this.zoom(1.1, centerX, centerY);
        },
        zoomOut: function() {
            const centerX = this.canvas.width / this.pixelRatio / 2;
            const centerY = this.canvas.height / this.pixelRatio / 2;
            if (!this.defaultFullScale && this.baseImage) {
                const viewports = {
                    full: {
                        x1: 57,
                        y1: 417,
                        x2: 1918,
                        y2: 1102
                    }
                };
                const viewport = viewports["full"];
                const viewWidth = Math.abs(viewport.x2 - viewport.x1);
                const viewHeight = Math.abs(viewport.y2 - viewport.y1);
                const canvasWidth = this.canvas.width / this.pixelRatio;
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const scaleX = canvasWidth / viewWidth;
                const scaleY = canvasHeight / viewHeight;
                this.defaultFullScale = Math.min(scaleX, scaleY) * .95;
            }
            if (this.defaultFullScale && Math.abs(this.scale - this.defaultFullScale) < .05) {
                this.zoom(.75, centerX, centerY);
            } else {
                this.zoom(.9, centerX, centerY);
            }
        }
    };
    window.ImageLayering = window.SunClipImageLayering;
    window.ConfiguratorCanvas = window.SunClipImageLayering;
})();